import React, { useEffect, useState, useMemo } from "react";
import AppLayout from "@/components/AppLayout";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { http, formatIDR, formatDate, whatsappHref } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import {
  Plus,
  X,
  Buildings,
  SquaresFour,
  ListBullets,
  PencilSimple,
  Trash,
  CalendarBlank,
  User,
  MagnifyingGlass,
  Target,
  Trophy,
  CurrencyCircleDollar,
  TrendUp,
  Tag,
  WhatsappLogo,
  HourglassMedium,
  Warning,
  Clock,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import SearchableContactSelect from "@/components/SearchableContactSelect";

/** Format numeric value into Indonesian Rupiah currency string (e.g. 150000000 -> "Rp 150.000.000") */
export function formatRupiahInput(val) {
  if (val === null || val === undefined || val === "") return "";
  const digits = String(val).replace(/\D/g, "");
  if (!digits) return "";
  const num = Number(digits);
  return `Rp ${new Intl.NumberFormat("id-ID").format(num)}`;
}

/** Parse formatted Rupiah string into raw numeric value */
export function parseRupiahInput(str) {
  if (!str) return 0;
  const digits = String(str).replace(/\D/g, "");
  return digits ? Number(digits) : 0;
}

/** Custom Rupiah currency input component */
export function RupiahInput({ value, onChange, placeholder = "Rp 0", className = "", ...props }) {
  const [displayValue, setDisplayValue] = useState(() => formatRupiahInput(value));

  useEffect(() => {
    setDisplayValue(formatRupiahInput(value));
  }, [value]);

  const handleChange = (e) => {
    const raw = e.target.value;
    const digits = raw.replace(/\D/g, "");
    if (!digits) {
      setDisplayValue("");
      onChange(0);
      return;
    }
    const num = Number(digits);
    setDisplayValue(formatRupiahInput(digits));
    onChange(num);
  };

  return (
    <input
      type="text"
      inputMode="numeric"
      value={displayValue}
      onChange={handleChange}
      placeholder={placeholder}
      className={className}
      {...props}
    />
  );
}

export const STAGES = [
  "lead",
  "quotation",
  "discuss_mgmt",
  "negotiation",
  "invoice",
  "payment",
  "pending",
  "lose",
];

export const ISO_SERVICES = [
  "ISO 9001",
  "ISO 14001",
  "ISO 45001",
  "ISO 37001",
  "ISO 27001",
];

export const ACCREDITATIONS = [
  "Non Akreditasi",
  "KAN",
];

export const stageColor = {
  lead: { bg: "#F1F5F9", text: "#475569", border: "#CBD5E1", dot: "#64748B", label: "Prospek" },
  quotation: { bg: "#E0F2FE", text: "#0369A1", border: "#BAE6FD", dot: "#0284C7", label: "Make an Offer / Quotation" },
  discuss_mgmt: { bg: "#EDE9FE", text: "#6D28D9", border: "#DDD6FE", dot: "#7C3AED", label: "Discuss Management" },
  negotiation: { bg: "#E0E7FF", text: "#3730A3", border: "#C7D2FE", dot: "#4F46E5", label: "Negotiation / Finalize proposal" },
  invoice: { bg: "#FEF3C7", text: "#B45309", border: "#FDE68A", dot: "#D97706", label: "Send Invoice / Waiting Payment" },
  payment: { bg: "#DCFCE7", text: "#166534", border: "#BBF7D0", dot: "#16A34A", label: "Payment" },
  pending: { bg: "#FFFBEB", text: "#92400E", border: "#FCD34D", dot: "#F59E0B", label: "Pending" },
  lose: { bg: "#FEE2E2", text: "#991B1B", border: "#FECACA", dot: "#DC2626", label: "Lose" },
};

export const STAGE_PROBABILITIES = {
  lead: 10,
  quotation: 20,
  discuss_mgmt: 40,
  negotiation: 60,
  invoice: 90,
  payment: 100,
  pending: 0,
  lose: 0,
};

export const inputCls =
  "w-full rounded-md border border-[#CBD5E1] bg-white px-3 py-2 text-sm text-[#173042] placeholder:text-slate-400 focus:border-[#173B4D] focus:outline-none focus:ring-1 focus:ring-[#173B4D] transition-colors";

export function Field({ label, children, required }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-[#475569] mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function PipelinePage() {
  const { t } = useI18n();
  const [deals, setDeals] = useState([]);
  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem("pipeline_view") || "board");
  const [search, setSearch] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("all");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [accreditationFilter, setAccreditationFilter] = useState("all");
  const [idleDays, setIdleDays] = useState(14);
  const [idleData, setIdleData] = useState(null);
  const [loadingIdle, setLoadingIdle] = useState(false);
  const [loading, setLoading] = useState(true);

  const setViewPersist = (v) => {
    setView(v);
    localStorage.setItem("pipeline_view", v);
  };

  const load = async () => {
    try {
      const [d, u, c] = await Promise.all([
        http.get("/deals"),
        http.get("/users"),
        http.get("/contacts"),
      ]);
      setDeals(d.data);
      setUsers(u.data);
      setContacts(c.data);
    } catch (err) {
      console.error("Load pipeline error:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadPipelineIdle = async (daysVal) => {
    setLoadingIdle(true);
    try {
      const { data } = await http.get(`/analytics/pipeline-idle?days=${daysVal}`);
      setIdleData(data);
    } catch (err) {
      console.error("Load pipeline idle error:", err);
    } finally {
      setLoadingIdle(false);
    }
  };

  useEffect(() => {
    load();
    loadPipelineIdle(idleDays);
  }, [idleDays]);

  const userMap = useMemo(
    () => users.reduce((acc, u) => ({ ...acc, [u.id]: u }), {}),
    [users]
  );

  const contactMap = useMemo(
    () => contacts.reduce((acc, c) => ({ ...acc, [c.id]: c }), {}),
    [contacts]
  );

  // Filtered deals for Board & Table views
  const filteredDeals = useMemo(() => {
    return deals.filter((d) => {
      const matchSearch =
        !search ||
        d.title.toLowerCase().includes(search.toLowerCase()) ||
        (d.company && d.company.toLowerCase().includes(search.toLowerCase()));
      const matchOwner = ownerFilter === "all" || d.owner_id === ownerFilter;
      const matchService = serviceFilter === "all" || d.service === serviceFilter;
      const matchAccreditation =
        accreditationFilter === "all" || d.accreditation === accreditationFilter;
      return matchSearch && matchOwner && matchService && matchAccreditation;
    });
  }, [deals, search, ownerFilter, serviceFilter, accreditationFilter]);

  // Filtered Pipeline Idle deals
  const filteredIdleDeals = useMemo(() => {
    if (!idleData?.items) return [];
    return idleData.items.filter((d) => {
      const matchSearch =
        !search ||
        d.title.toLowerCase().includes(search.toLowerCase()) ||
        (d.company && d.company.toLowerCase().includes(search.toLowerCase()));
      const matchOwner = ownerFilter === "all" || d.owner_id === ownerFilter;
      const matchService = serviceFilter === "all" || d.service === serviceFilter;
      const matchAccreditation =
        accreditationFilter === "all" || d.accreditation === accreditationFilter;
      return matchSearch && matchOwner && matchService && matchAccreditation;
    });
  }, [idleData, search, ownerFilter, serviceFilter, accreditationFilter]);

  // Stage Grouping
  const grouped = useMemo(() => {
    return STAGES.reduce((acc, s) => {
      acc[s] = filteredDeals.filter((d) => d.stage === s);
      return acc;
    }, {});
  }, [filteredDeals]);

  // Pipeline Metrics Calculation
  const metrics = useMemo(() => {
    const activeDeals = deals.filter((d) => d.stage !== "payment" && d.stage !== "lose");
    const totalPipeline = activeDeals.reduce((sum, d) => sum + (Number(d.value) || 0), 0);
    const wonDeals = deals.filter((d) => d.stage === "payment");
    const totalWon = wonDeals.reduce((sum, d) => sum + (Number(d.value) || 0), 0);

    const weightedPipeline = activeDeals.reduce((sum, d) => {
      const prob = STAGE_PROBABILITIES[d.stage] !== undefined ? STAGE_PROBABILITIES[d.stage] : 0;
      return sum + ((Number(d.value) || 0) * prob) / 100;
    }, 0);

    return {
      totalPipeline,
      activeCount: activeDeals.length,
      weightedPipeline,
      totalWon,
    };
  }, [deals]);

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    const { draggableId, destination, source } = result;
    if (source.droppableId === destination.droppableId) return;
    const newStage = destination.droppableId;

    setDeals((prev) =>
      prev.map((d) => (d.id === draggableId ? { ...d, stage: newStage } : d))
    );

    try {
      await http.patch(`/deals/${draggableId}/stage`, { stage: newStage });
      toast.success(`Deal dipindah ke ${stageColor[newStage]?.label || newStage}`);
      loadPipelineIdle(idleDays);
    } catch {
      toast.error("Gagal memindah deal");
      load();
    }
  };

  const del = async (id) => {
    if (!window.confirm("Hapus deal ini?")) return;
    try {
      await http.delete(`/deals/${id}`);
      toast.success("Deal berhasil dihapus");
      load();
      loadPipelineIdle(idleDays);
    } catch {
      toast.error("Gagal menghapus deal");
    }
  };

  const idleSeverity = (n) =>
    n >= 60 ? "#D63230" : n >= 30 ? "#F9A523" : n >= 14 ? "#C28E0E" : "#5A6B76";

  return (
    <AppLayout>
      {/* 1. Pipeline Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-5 gap-3.5">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#173042]" data-testid="pipeline-title">
            Pipeline
          </h1>
          <p className="text-xs text-[#64748B] mt-0.5">
            Pantau peluang sertifikasi ISO dari prospek, proposal, negosiasi, hingga won.
          </p>
        </div>

        {/* Action Controls */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Search Box */}
          <div className="relative flex-1 sm:flex-initial min-w-[130px]">
            <MagnifyingGlass
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="Cari deal / PT..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full sm:w-40 lg:w-48 pl-8 pr-2.5 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs text-[#173042] placeholder:text-slate-400 focus:outline-none focus:border-[#173B4D] focus:ring-1 focus:ring-[#173B4D]"
            />
          </div>

          {/* Owner Filter */}
          {users.length > 0 && (
            <div className="relative">
              <select
                value={ownerFilter}
                onChange={(e) => setOwnerFilter(e.target.value)}
                data-testid="filter-owner"
                className="pl-2.5 pr-6 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#475569] focus:outline-none focus:border-[#173B4D]"
              >
                <option value="all">Semua Sales</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* ISO Service Filter */}
          <div className="relative">
            <select
              value={serviceFilter}
              onChange={(e) => setServiceFilter(e.target.value)}
              data-testid="filter-service"
              className="pl-2.5 pr-6 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#475569] focus:outline-none focus:border-[#173B4D]"
            >
              <option value="all">Semua ISO</option>
              {ISO_SERVICES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Accreditation Filter */}
          <div className="relative">
            <select
              value={accreditationFilter}
              onChange={(e) => setAccreditationFilter(e.target.value)}
              data-testid="filter-accreditation"
              className="pl-2.5 pr-6 py-1.5 bg-white border border-[#CBD5E1] rounded-lg text-xs font-medium text-[#475569] focus:outline-none focus:border-[#173B4D]"
            >
              <option value="all">Semua Akreditasi</option>
              {ACCREDITATIONS.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>

          {/* View Toggle */}
          <div
            className="flex bg-white rounded-lg border border-[#CBD5E1] p-0.5 shadow-2xs"
            data-testid="pipeline-view-toggle"
          >
            <button
              onClick={() => setViewPersist("board")}
              data-testid="view-board-btn"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                view === "board"
                  ? "bg-[#173B4D] text-white shadow-xs"
                  : "text-[#64748B] hover:text-[#173042]"
              }`}
            >
              <SquaresFour size={14} weight="bold" />
              <span>Board</span>
            </button>
            <button
              onClick={() => setViewPersist("table")}
              data-testid="view-table-btn"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                view === "table"
                  ? "bg-[#173B4D] text-white shadow-xs"
                  : "text-[#64748B] hover:text-[#173042]"
              }`}
            >
              <ListBullets size={14} weight="bold" />
              <span>Tabel</span>
            </button>
            <button
              onClick={() => setViewPersist("idle")}
              data-testid="view-idle-btn"
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                view === "idle"
                  ? "bg-[#D9A321] text-white shadow-xs"
                  : "text-[#64748B] hover:text-[#173042]"
              }`}
            >
              <HourglassMedium size={14} weight="bold" />
              <span>Pipeline Idle</span>
              {idleData?.count > 0 && (
                <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.2 rounded-full font-bold leading-tight">
                  {idleData.count}
                </span>
              )}
            </button>
          </div>

          {/* Create Deal Button */}
          <button
            onClick={() => {
              setEditing(null);
              setShowModal(true);
            }}
            data-testid="new-deal-btn"
            className="bg-[#173B4D] hover:bg-[#112B38] text-white px-3.5 py-1.5 rounded-lg flex items-center gap-1.5 text-xs font-semibold shadow-xs transition-colors"
          >
            <Plus size={14} weight="bold" />
            <span>{t("action.newDeal")}</span>
          </button>
        </div>
      </div>

      {/* 2. Compact Pipeline Summary Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 bg-white rounded-lg border border-[#E2E8F0] shadow-2xs mb-5 divide-y sm:divide-y-0 sm:divide-x divide-[#E2E8F0] overflow-hidden">
        <div className="p-2.5 sm:px-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
              Total Pipeline
            </div>
            <div className="font-mono text-sm sm:text-base font-bold text-[#173042] mt-0.5">
              {formatIDR(metrics.totalPipeline)}
            </div>
          </div>
          <Target size={18} className="text-[#173B4D] opacity-60" />
        </div>

        <div className="p-2.5 sm:px-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
              Active Deals
            </div>
            <div className="font-mono text-sm sm:text-base font-bold text-[#173042] mt-0.5">
              {metrics.activeCount}
            </div>
          </div>
          <CurrencyCircleDollar size={18} className="text-[#0F7180] opacity-60" />
        </div>

        <div className="p-2.5 sm:px-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
              Weighted Pipeline
            </div>
            <div className="font-mono text-sm sm:text-base font-bold text-[#0F7180] mt-0.5">
              {formatIDR(metrics.weightedPipeline)}
            </div>
          </div>
          <TrendUp size={18} className="text-[#0F7180] opacity-60" />
        </div>

        <div className="p-2.5 sm:px-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#64748B]">
              Won Deals
            </div>
            <div className="font-mono text-sm sm:text-base font-bold text-[#2E8B57] mt-0.5">
              {formatIDR(metrics.totalWon)}
            </div>
          </div>
          <Trophy size={18} className="text-[#2E8B57] opacity-60" />
        </div>
      </div>

      {/* 3. Main Workspace Area: Kanban Board vs Table vs Pipeline Idle */}
      {view === "idle" ? (
        /* ================= PIPELINE IDLE VIEW ================= */
        <div className="space-y-4" data-testid="pipeline-idle-view">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-3.5 sm:p-4 rounded-xl border border-[#E2E8F0] shadow-2xs">
            <div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#D9A321] animate-pulse" />
                <h3 className="font-bold text-sm sm:text-base text-[#173042]">
                  Pipeline Idle (Tanpa Aktivitas 14+ Hari)
                </h3>
              </div>
              <p className="text-xs text-[#64748B] mt-0.5">
                Peluang sales aktif yang belum ada aktivitas qualifying sales (meeting, call, task, catatan) selama minimal {idleDays} hari berturut-turut.
              </p>
            </div>

            {/* Threshold Selector */}
            <div className="flex items-center gap-1.5 self-start sm:self-auto">
              <span className="text-[10px] uppercase font-bold text-[#64748B] mr-1">Ambang Batas:</span>
              {[14, 30, 60].map((d) => (
                <button
                  key={d}
                  onClick={() => setIdleDays(d)}
                  data-testid={`idle-threshold-${d}`}
                  className={`px-3 py-1.5 rounded-lg border text-xs font-mono font-bold transition-colors ${
                    idleDays === d
                      ? "bg-[#173B4D] border-[#173B4D] text-white shadow-2xs"
                      : "border-[#CBD5E1] bg-white text-[#64748B] hover:border-[#173B4D]"
                  }`}
                >
                  {d}d+
                </button>
              ))}
            </div>
          </div>

          {loadingIdle ? (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center text-xs text-[#64748B] font-semibold uppercase tracking-wider">
              Memuat data Pipeline Idle...
            </div>
          ) : filteredIdleDeals.length === 0 ? (
            <div className="bg-white rounded-xl border border-[#E2E8F0] p-12 text-center text-sm text-[#64748B]">
              <div className="font-bold text-base text-[#173042] mb-1">
                Luar biasa! Tidak ada deal idle.
              </div>
              <div>Semua peluang pipeline memiliki aktivitas sales dalam {idleDays} hari terakhir.</div>
            </div>
          ) : (
            <>
              {/* Mobile Card List (< md) */}
              <div className="md:hidden space-y-3" data-testid="pipeline-idle-mobile-list">
                {filteredIdleDeals.map((d) => {
                  const contact = contactMap[d.contact_id];
                  const phone = d.contact_phone || contact?.phone;
                  return (
                    <div
                      key={d.id}
                      className="bg-white rounded-xl border border-[#E2E8F0] p-4 shadow-2xs space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-bold text-sm text-[#173042]">{d.title}</div>
                          {d.company && (
                            <div className="text-xs text-[#64748B] mt-0.5">{d.company}</div>
                          )}
                        </div>
                        <span
                          className="font-mono text-xs font-bold px-2 py-0.5 rounded-full bg-red-50 shrink-0"
                          style={{ color: idleSeverity(d.days_idle) }}
                        >
                          {d.days_idle} hari idle
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-1.5">
                        <span
                          className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                          style={{
                            backgroundColor: stageColor[d.stage]?.bg || "#F1F5F9",
                            color: stageColor[d.stage]?.text || "#475569",
                          }}
                        >
                          {stageColor[d.stage]?.label || d.stage}
                        </span>
                        {d.service && (
                          <span className="text-[10px] font-bold bg-[#173B4D]/5 text-[#173B4D] border border-[#173B4D]/20 px-1.5 py-0.5 rounded">
                            {d.service}
                          </span>
                        )}
                        {d.accreditation && (
                          <span className="text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                            {d.accreditation}
                          </span>
                        )}
                        <span className="font-mono text-xs font-bold text-[#173042] ml-auto">
                          {formatIDR(d.value)}
                        </span>
                      </div>

                      <div className="flex items-center justify-between gap-2 pt-2 border-t border-[#F1F5F9] text-xs">
                        <div className="text-[11px] text-[#64748B]">
                          Terakhir: {formatDate(d.last_activity_at)} ({d.last_activity_type})
                        </div>
                        <div className="flex items-center gap-1">
                          {phone && (
                            <a
                              href={
                                whatsappHref(phone) +
                                `?text=${encodeURIComponent(
                                  `Halo ${d.contact_name || d.company || "Bapak/Ibu"}, saya dari Nusacert ingin follow-up terkait ${d.title}. Apakah ada waktu untuk kami update kembali progress sertifikasi ISO?`
                                )}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 bg-[#2E8B57] hover:bg-[#246B43] text-white px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors"
                            >
                              <WhatsappLogo size={13} weight="fill" />
                              <span>Follow Up</span>
                            </a>
                          )}
                          <button
                            onClick={() => {
                              setEditing(d);
                              setShowModal(true);
                            }}
                            className="p-1 rounded text-[#64748B] hover:text-[#173B4D] hover:bg-[#F1F5F9]"
                            title="Edit Deal"
                          >
                            <PencilSimple size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Desktop Table (>= md) */}
              <div className="hidden md:block bg-white rounded-xl border border-[#E2E8F0] shadow-2xs overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs" data-testid="pipeline-idle-table">
                    <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0] uppercase tracking-wider text-[#64748B] font-semibold">
                      <tr>
                        <th className="px-4 py-3.5">Judul Deal & Perusahaan</th>
                        <th className="px-4 py-3.5">Tahap</th>
                        <th className="px-4 py-3.5">Layanan & Akreditasi</th>
                        <th className="px-4 py-3.5 text-right">Nilai Deal</th>
                        <th className="px-4 py-3.5">Sales Owner</th>
                        <th className="px-4 py-3.5">Aktivitas Terakhir</th>
                        <th className="px-4 py-3.5 text-right">Idle</th>
                        <th className="px-4 py-3.5 text-right">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#E2E8F0]">
                      {filteredIdleDeals.map((d) => {
                        const colorInfo = stageColor[d.stage] || stageColor.lead;
                        const contact = contactMap[d.contact_id];
                        const phone = d.contact_phone || contact?.phone;

                        return (
                          <tr key={d.id} className="hover:bg-[#F8FAFC] transition-colors">
                            <td className="px-4 py-3.5">
                              <div className="font-bold text-[#173042]">{d.title}</div>
                              <div className="text-[11px] text-[#64748B]">{d.company || "-"}</div>
                            </td>
                            <td className="px-4 py-3.5">
                              <span
                                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-[11px]"
                                style={{
                                  backgroundColor: colorInfo.bg,
                                  color: colorInfo.text,
                                  border: `1px solid ${colorInfo.border}`,
                                }}
                              >
                                <span
                                  className="w-1.5 h-1.5 rounded-full"
                                  style={{ backgroundColor: colorInfo.dot }}
                                />
                                {t(`stage.${d.stage}`)}
                              </span>
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="flex flex-wrap items-center gap-1">
                                {d.service && (
                                  <span className="text-[10px] font-bold bg-[#173B4D]/5 text-[#173B4D] border border-[#173B4D]/20 px-1.5 py-0.5 rounded">
                                    {d.service}
                                  </span>
                                )}
                                {d.accreditation && (
                                  <span className="text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                                    {d.accreditation}
                                  </span>
                                )}
                                {!d.service && !d.accreditation && (
                                  <span className="text-slate-400">-</span>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3.5 font-mono font-bold text-right text-[#173042]">
                              {formatIDR(d.value)}
                            </td>
                            <td className="px-4 py-3.5 text-[#64748B]">
                              {d.owner_name || userMap[d.owner_id]?.name || "Sales"}
                            </td>
                            <td className="px-4 py-3.5">
                              <div className="font-mono text-[11px] text-[#173042]">
                                {formatDate(d.last_activity_at)}
                              </div>
                              <div className="text-[10px] text-[#64748B] capitalize">
                                ({d.last_activity_type})
                              </div>
                            </td>
                            <td className="px-4 py-3.5 text-right">
                              <span
                                className="font-mono text-xs font-bold px-2 py-0.5 rounded-full bg-red-50"
                                style={{ color: idleSeverity(d.days_idle) }}
                              >
                                {d.days_idle} hari
                              </span>
                            </td>
                            <td className="px-4 py-3.5 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                {phone && (
                                  <a
                                    href={
                                      whatsappHref(phone) +
                                      `?text=${encodeURIComponent(
                                        `Halo ${d.contact_name || d.company || "Bapak/Ibu"}, saya dari Nusacert ingin follow up terkait ${d.title}. Apakah ada waktu untuk kami update kembali progress sertifikasi ISO?`
                                      )}`
                                    }
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 bg-[#2E8B57] hover:bg-[#246B43] text-white px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors"
                                    title="Follow up WhatsApp"
                                  >
                                    <WhatsappLogo size={13} weight="fill" />
                                    <span>WA</span>
                                  </a>
                                )}
                                <button
                                  onClick={() => {
                                    setEditing(d);
                                    setShowModal(true);
                                  }}
                                  className="p-1.5 rounded text-[#64748B] hover:text-[#173B4D] hover:bg-[#F1F5F9]"
                                  title="Edit Deal"
                                >
                                  <PencilSimple size={15} />
                                </button>
                                <button
                                  onClick={() => del(d.id)}
                                  className="p-1.5 rounded text-[#64748B] hover:text-red-600 hover:bg-red-50"
                                  title="Hapus Deal"
                                >
                                  <Trash size={15} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      ) : view === "board" ? (
        /* ================= KANBAN BOARD VIEW ================= */
        <DragDropContext onDragEnd={onDragEnd}>
          <div className="overflow-x-auto pb-6 scrollbar-thin -mx-3.5 sm:-mx-6 lg:-mx-8 px-3.5 sm:px-6 lg:px-8">
            <div className="flex gap-3.5 min-w-max">
              {STAGES.map((s) => {
                const items = grouped[s] || [];
                const totalVal = items.reduce((acc, d) => acc + (Number(d.value) || 0), 0);
                const colorInfo = stageColor[s] || stageColor.lead;

                return (
                  <div
                    key={s}
                    className="w-[260px] sm:w-[270px] shrink-0 bg-[#F5F7F9] rounded-xl border border-[#E2E8F0]/80 p-2.5 flex flex-col"
                  >
                    {/* Compact Stage Header */}
                    <div className="px-2.5 py-2 mb-2 bg-white rounded-lg border border-[#E2E8F0] shadow-2xs">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-1.5">
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: colorInfo.dot }}
                          />
                          <span className="text-[11px] font-bold uppercase tracking-wider text-[#173042]">
                            {t(`stage.${s}`)}
                          </span>
                        </div>
                        <span className="font-mono text-[10px] font-bold text-[#64748B] bg-[#F1F5F9] px-1.5 py-0.5 rounded-full">
                          {items.length}
                        </span>
                      </div>
                      <div className="font-mono text-[11px] font-bold text-[#173042] mt-1">
                        {formatIDR(totalVal)}
                      </div>
                    </div>

                    {/* Droppable Card Stack */}
                    <Droppable droppableId={s}>
                      {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={`flex-1 space-y-2.5 min-h-[160px] p-0.5 rounded-lg transition-colors ${
                            snapshot.isDraggingOver ? "bg-[#E2E8F0]/40" : ""
                          }`}
                        >
                          {items.map((d, idx) => {
                            const owner = userMap[d.owner_id];
                            return (
                              <Draggable key={d.id} draggableId={d.id} index={idx}>
                                {(p, snap) => (
                                  <div
                                    ref={p.innerRef}
                                    {...p.draggableProps}
                                    {...p.dragHandleProps}
                                    className={`group relative bg-white rounded-lg border border-[#E2E8F0] p-3 shadow-2xs hover:shadow-card-hover hover:border-[#CBD5E1] transition-all flex flex-col justify-between cursor-grab active:cursor-grabbing ${
                                      snap.isDragging ? "shadow-lg rotate-1 border-[#173B4D] z-50" : ""
                                    }`}
                                  >
                                    <div>
                                      {/* Top Row: Title + Action Menu */}
                                      <div className="flex items-start justify-between gap-1.5">
                                        <h4 className="font-bold text-[#173042] text-xs sm:text-[13px] leading-snug break-words flex-1 line-clamp-2">
                                          {d.title}
                                        </h4>
                                        <div className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex items-center gap-0.5 shrink-0">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setEditing(d);
                                              setShowModal(true);
                                            }}
                                            data-testid={`edit-deal-${d.id}`}
                                            className="p-1 rounded text-[#64748B] hover:text-[#173042] hover:bg-[#F1F5F9]"
                                            title="Edit Deal"
                                          >
                                            <PencilSimple size={14} />
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              del(d.id);
                                            }}
                                            data-testid={`delete-deal-${d.id}`}
                                            className="p-1 rounded text-[#64748B] hover:text-red-600 hover:bg-red-50"
                                            title="Hapus Deal"
                                          >
                                            <Trash size={14} />
                                          </button>
                                        </div>
                                      </div>

                                      {/* Secondary: Company Name */}
                                      {d.company && (
                                        <div className="flex items-center gap-1 text-[11px] text-[#64748B] mt-1 font-medium">
                                          <Buildings size={12} className="shrink-0 text-slate-400" />
                                          <span className="truncate">{d.company}</span>
                                        </div>
                                      )}

                                      {/* ISO Service & Accreditation badges */}
                                      {(d.service || d.accreditation) && (
                                        <div className="flex flex-wrap items-center gap-1 mt-2">
                                          {d.service && (
                                            <span className="text-[9px] uppercase font-bold bg-[#173B4D]/5 text-[#173B4D] border border-[#173B4D]/20 px-1.5 py-0.5 rounded">
                                              {d.service}
                                            </span>
                                          )}
                                          {d.accreditation && (
                                            <span className="text-[9px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                                              {d.accreditation}
                                            </span>
                                          )}
                                        </div>
                                      )}
                                    </div>

                                    {/* Financial & Probability Row */}
                                    <div className="mt-2.5 pt-2 border-t border-[#F1F5F9] flex items-center justify-between">
                                      <div className="font-mono text-xs font-bold text-[#173042]">
                                        {formatIDR(d.value)}
                                      </div>
                                      {d.probability !== null && d.probability !== undefined && (
                                        <span className="font-mono text-[10px] font-bold text-[#0F7180] bg-[#0F7180]/10 px-1.5 py-0.5 rounded">
                                          {d.probability}%
                                        </span>
                                      )}
                                    </div>

                                    {/* Metadata Footer: Owner & Target Date */}
                                    <div className="mt-1.5 pt-1.5 border-t border-[#F8FAFC] flex items-center justify-between text-[11px] text-[#64748B]">
                                      <div className="flex items-center gap-1.5 truncate">
                                        {owner?.avatar_url ? (
                                          <img
                                            src={owner.avatar_url}
                                            alt={owner.name}
                                            className="w-4 h-4 rounded-full object-cover"
                                          />
                                        ) : (
                                          <User size={12} className="text-slate-400" />
                                        )}
                                        <span className="truncate max-w-[90px]">
                                          {owner?.name || "Sales"}
                                        </span>
                                      </div>

                                      {d.expected_close && (
                                        <div className="flex items-center gap-1 text-[10px] font-mono text-slate-500">
                                          <CalendarBlank size={11} />
                                          <span>{formatDate(d.expected_close)}</span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                          {items.length === 0 && (
                            <div className="h-16 flex items-center justify-center text-[11px] text-slate-400 italic">
                              Kosong
                            </div>
                          )}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}
            </div>
          </div>
        </DragDropContext>
      ) : (
        /* ================= TABLE VIEW ================= */
        <div className="bg-white rounded-lg border border-[#E2E8F0] shadow-2xs overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs" data-testid="pipeline-table">
              <thead className="bg-[#F8FAFC] border-b border-[#E2E8F0] uppercase tracking-wider text-[#64748B] font-semibold">
                <tr>
                  <th className="px-4 py-3.5">Judul Deal</th>
                  <th className="px-4 py-3.5">Perusahaan</th>
                  <th className="px-4 py-3.5">Tahap</th>
                  <th className="px-4 py-3.5">Layanan & Akreditasi</th>
                  <th className="px-4 py-3.5 text-right">Nilai Deal</th>
                  <th className="px-4 py-3.5 text-center">Probabilitas</th>
                  <th className="px-4 py-3.5">Sales Owner</th>
                  <th className="px-4 py-3.5 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {filteredDeals.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-12 text-[#64748B]">
                      Tidak ada deal yang sesuai dengan filter
                    </td>
                  </tr>
                ) : (
                  filteredDeals.map((d) => {
                    const colorInfo = stageColor[d.stage] || stageColor.lead;
                    return (
                      <tr key={d.id} className="hover:bg-[#F8FAFC] transition-colors">
                        <td className="px-4 py-3.5 font-bold text-[#173042]">
                          {d.title}
                        </td>
                        <td className="px-4 py-3.5 text-[#64748B]">
                          {d.company || "-"}
                        </td>
                        <td className="px-4 py-3.5">
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full font-semibold text-[11px]"
                            style={{
                              backgroundColor: colorInfo.bg,
                              color: colorInfo.text,
                              border: `1px solid ${colorInfo.border}`,
                            }}
                          >
                            <span
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: colorInfo.dot }}
                            />
                            {t(`stage.${d.stage}`)}
                          </span>
                        </td>
                        <td className="px-4 py-3.5">
                          <div className="flex flex-wrap items-center gap-1">
                            {d.service && (
                              <span className="text-[10px] font-bold bg-[#173B4D]/5 text-[#173B4D] border border-[#173B4D]/20 px-1.5 py-0.5 rounded">
                                {d.service}
                              </span>
                            )}
                            {d.accreditation && (
                              <span className="text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                                {d.accreditation}
                              </span>
                            )}
                            {!d.service && !d.accreditation && (
                              <span className="text-slate-400">-</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3.5 font-mono font-bold text-right text-[#173042]">
                          {formatIDR(d.value)}
                        </td>
                        <td className="px-4 py-3.5 font-mono font-semibold text-center text-[#0F7180]">
                          {d.probability !== null && d.probability !== undefined
                            ? `${d.probability}%`
                            : "-"}
                        </td>
                        <td className="px-4 py-3.5 text-[#64748B]">
                          {userMap[d.owner_id]?.name || "Sales"}
                        </td>
                        <td className="px-4 py-3.5 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => {
                                setEditing(d);
                                setShowModal(true);
                              }}
                              className="p-1.5 rounded text-[#64748B] hover:text-[#173B4D] hover:bg-[#F1F5F9]"
                              title="Edit Deal"
                            >
                              <PencilSimple size={15} />
                            </button>
                            <button
                              onClick={() => del(d.id)}
                              className="p-1.5 rounded text-[#64748B] hover:text-red-600 hover:bg-red-50"
                              title="Hapus Deal"
                            >
                              <Trash size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Deal Modal Form */}
      {showModal && (
        <DealModal
          initial={editing}
          users={users}
          contacts={contacts}
          onClose={() => setShowModal(false)}
          onSaved={() => {
            setShowModal(false);
            load();
            loadPipelineIdle(idleDays);
          }}
        />
      )}
    </AppLayout>
  );
}

function DealModal({ onClose, onSaved, initial, users, contacts }) {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const initialStage = initial?.stage || "lead";
  const [form, setForm] = useState({
    title: "",
    company: "",
    value: 0,
    stage: initialStage,
    probability: STAGE_PROBABILITIES[initialStage] !== undefined ? STAGE_PROBABILITIES[initialStage] : 10,
    expected_close: "",
    contact_id: "",
    owner_id: "",
    service: "",
    accreditation: "",
    notes: "",
    ...(initial
      ? {
          ...initial,
          expected_close: initial.expected_close
            ? initial.expected_close.slice(0, 10)
            : "",
          contact_id: initial.contact_id || "",
          owner_id: initial.owner_id || "",
          service: initial.service || "",
          accreditation: initial.accreditation || "",
          probability: STAGE_PROBABILITIES[initial.stage] !== undefined ? STAGE_PROBABILITIES[initial.stage] : 10,
        }
      : {}),
  });
  const [saving, setSaving] = useState(false);

  // WhatsApp Follow Up calculation
  const selectedContact = contacts.find((c) => c.id === form.contact_id);
  const contactName = selectedContact?.name || form.company || "Bapak/Ibu";
  const salesName = me?.name || "Sales Nusacert";
  const dealTitle = form.title || initial?.title || "Sertifikasi ISO";
  const waPhone = selectedContact?.phone;
  const waMessage = `Halo Pak/Bu ${contactName}, saya ${salesName} dari Nusacert. Saya ingin follow up terkait ${dealTitle}. Apakah ada waktu yang nyaman untuk kami diskusikan kembali?`;
  const waUrl = waPhone
    ? `https://wa.me/${waPhone}?text=${encodeURIComponent(waMessage)}`
    : null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: form.title,
        company: form.company || undefined,
        value: Number(form.value) || 0,
        stage: form.stage,
        probability:
          form.probability === "" ? undefined : Number(form.probability),
        expected_close: form.expected_close
          ? new Date(form.expected_close).toISOString()
          : undefined,
        contact_id: form.contact_id || undefined,
        owner_id: form.owner_id || undefined,
        service: form.service || undefined,
        accreditation: form.accreditation || undefined,
        notes: form.notes || undefined,
      };

      if (initial) {
        await http.patch(`/deals/${initial.id}`, payload);
        toast.success("Deal berhasil diperbarui");
      } else {
        await http.post("/deals", payload);
        toast.success("Deal baru berhasil dibuat");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan deal");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl border border-[#E2E8F0] w-full max-w-xl max-h-[92vh] flex flex-col overflow-hidden page-enter">
        <div className="px-4 sm:px-6 py-3.5 sm:py-4 border-b border-[#E2E8F0] flex items-center justify-between bg-[#F8FAFC] shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-sm sm:text-base text-[#173042]">
                {initial ? "Edit Deal" : "Tambah Deal Baru"}
              </h3>
              {initial && (
                <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider bg-[#173B4D] text-white px-1.5 sm:px-2 py-0.5 rounded">
                  {t(`stage.${form.stage}`)}
                </span>
              )}
            </div>
            <p className="text-[11px] sm:text-xs text-[#64748B] mt-0.5">
              Kelola detail deal dan proyeksi sertifikasi ISO
            </p>
          </div>

          <div className="flex items-center gap-2">
            {initial && (
              waUrl ? (
                <a
                  href={waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="deal-wa-followup-btn"
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg bg-[#2E8B57] hover:bg-[#246B43] text-white text-[11px] sm:text-xs font-semibold shadow-xs transition-colors"
                  title="Buka WhatsApp dengan pesan follow-up"
                >
                  <WhatsappLogo size={14} weight="fill" className="shrink-0" />
                  <span className="hidden sm:inline">Follow Up WhatsApp</span>
                  <span className="sm:hidden">WhatsApp</span>
                </a>
              ) : (
                <button
                  type="button"
                  disabled
                  data-testid="deal-wa-followup-btn-disabled"
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-slate-400 text-[11px] sm:text-xs font-medium cursor-not-allowed"
                  title={form.contact_id ? "Kontak ini belum memiliki nomor WhatsApp valid" : "Pilih kontak terkait untuk mengaktifkan WhatsApp Follow-up"}
                >
                  <WhatsappLogo size={14} weight="fill" className="shrink-0" />
                  <span className="hidden sm:inline">Follow Up WA (Tidak ada No. WA)</span>
                  <span className="sm:hidden">No WA</span>
                </button>
              )
            )}
            <button
              onClick={onClose}
              className="p-1 rounded text-[#64748B] hover:text-[#173042] hover:bg-[#E2E8F0]"
            >
              <X size={18} weight="bold" />
            </button>
          </div>
        </div>

        <form onSubmit={submit} className="flex flex-col flex-1 overflow-hidden" data-testid="deal-form">
          <div className="p-4 sm:p-6 space-y-3.5 sm:space-y-4 overflow-y-auto flex-1">
            <Field label="Judul Deal" required>
              <input
                required
                data-testid="deal-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className={inputCls}
                placeholder="Contoh: Sertifikasi ISO 9001 PT Nusa Karya"
              />
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Field label="Perusahaan">
                <input
                  data-testid="deal-company"
                  value={form.company || ""}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className={inputCls}
                  placeholder="Nama Perusahaan"
                />
              </Field>

              <Field label="Nilai Deal (IDR)">
                <RupiahInput
                  data-testid="deal-value"
                  value={form.value}
                  onChange={(numVal) => setForm({ ...form, value: numVal })}
                  className={inputCls}
                  placeholder="Rp 0"
                />
              </Field>
            </div>

            {/* ISO Service & Accreditation Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Field label="Layanan ISO">
                <select
                  value={form.service || ""}
                  onChange={(e) => setForm({ ...form, service: e.target.value })}
                  className={inputCls}
                  data-testid="deal-service"
                >
                  <option value="">-- Pilih Layanan ISO --</option>
                  {ISO_SERVICES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Akreditasi">
                <select
                  value={form.accreditation || ""}
                  onChange={(e) => setForm({ ...form, accreditation: e.target.value })}
                  className={inputCls}
                  data-testid="deal-accreditation"
                >
                  <option value="">-- Pilih Akreditasi --</option>
                  {ACCREDITATIONS.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Field label="Tahap Pipeline">
                <select
                  value={form.stage}
                  onChange={(e) => {
                    const newStage = e.target.value;
                    const newProb = STAGE_PROBABILITIES[newStage] !== undefined ? STAGE_PROBABILITIES[newStage] : 10;
                    setForm({ ...form, stage: newStage, probability: newProb });
                  }}
                  className={inputCls}
                  data-testid="deal-stage"
                >
                  {STAGES.map((s) => (
                    <option key={s} value={s}>
                      {t(`stage.${s}`)}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Probabilitas (%)">
                <div
                  className="w-full rounded-md border border-[#CBD5E1] bg-slate-50 px-3 py-2 text-sm font-semibold text-[#173042] flex items-center justify-between cursor-not-allowed select-none"
                  data-testid="deal-probability-display"
                  title="Probabilitas ditentukan otomatis berdasarkan tahap pipeline"
                >
                  <span>{form.probability ?? STAGE_PROBABILITIES[form.stage] ?? 10}%</span>
                  <span className="text-[11px] font-medium text-slate-500 bg-slate-200/80 px-2 py-0.5 rounded">
                    Otomatis
                  </span>
                </div>
              </Field>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <Field label="Target Tutup (Closing)">
                <input
                  type="date"
                  value={form.expected_close}
                  onChange={(e) =>
                    setForm({ ...form, expected_close: e.target.value })
                  }
                  className={inputCls}
                />
              </Field>

              <Field label="Kontak Terkait">
                <SearchableContactSelect
                  contacts={contacts}
                  value={form.contact_id}
                  onChange={(id) => setForm({ ...form, contact_id: id })}
                  autoFillCompany={!form.company}
                  onCompanySelect={(comp) => {
                    if (!form.company) setForm((prev) => ({ ...prev, company: comp }));
                  }}
                  placeholder="Cari nama kontak atau perusahaan..."
                />
              </Field>
            </div>

            {users.length > 0 && (
              <Field label="Sales Owner">
                <select
                  value={form.owner_id}
                  onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                  className={inputCls}
                >
                  <option value="">-- Default Sales --</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.role})
                    </option>
                  ))}
                </select>
              </Field>
            )}

            <Field label="Catatan Tambahan">
              <textarea
                rows={3}
                value={form.notes || ""}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className={inputCls}
                placeholder="Catatan kebutuhan sertifikasi, scope audit, dsb."
              />
            </Field>
          </div>

          <div className="px-4 sm:px-6 py-3 border-t border-[#E2E8F0] bg-[#F8FAFC] flex items-center justify-end gap-2.5 shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-[#CBD5E1] text-xs font-semibold text-[#475569] hover:bg-[#F8FAFC] transition-colors"
            >
              {t("action.cancel")}
            </button>
            <button
              disabled={saving}
              type="submit"
              data-testid="deal-submit"
              className="bg-[#173B4D] hover:bg-[#112B38] text-white px-5 py-2 rounded-lg text-xs font-semibold disabled:opacity-50 transition-colors shadow-xs"
            >
              {saving ? "Menyimpan..." : initial ? "Simpan Perubahan" : "Buat Deal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
